import React, { useRef, useState } from 'react';
import { clsx } from 'clsx';
import type { RoastStageState } from './types';
import { useLanguage } from '../../../features/language/useLanguage';
import { MAX_ROAST_TEMP_C, clampTemperatureInput } from './RoastProfileUtils';

interface RoastStageDataSectionProps {
  stages: RoastStageState[];
  onStageChange: (index: number, field: keyof RoastStageState, value: string | number) => void;
  readOnly: boolean;
  showTargetLabels: boolean;
}

export const RoastStageDataSection: React.FC<RoastStageDataSectionProps> = ({
  stages,
  onStageChange,
  readOnly,
  showTargetLabels,
}) => {
  const { t } = useLanguage();
  const [activeTimeKey, setActiveTimeKey] = useState<RoastStageState['key'] | null>(null);
  const [timeDrafts, setTimeDrafts] = useState<Record<string, string>>({});
  const timeInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const getTimeDigits = (raw: string) => raw.replace(/\D/g, '').slice(0, 4);

  const formatStageTimeFromDigits = (digits: string) => {
    const d = digits.replace(/\D/g, '').slice(0, 4);
    if (d.length === 0) return '';
    if (d.length === 1) return `0${d}:00`;
    if (d.length === 2) return `${d}:00`;
    if (d.length === 3) return `0${d.slice(0, 1)}:${d.slice(1)}`;
    return `${d.slice(0, 2)}:${d.slice(2)}`;
  };

  const ensureCaretAtEnd = (key: string) => {
    const el = timeInputRefs.current[key];
    if (!el) return;
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      return;
    }
  };

  const renderTimeOverlay = (digitsRaw: string) => {
    const digits = digitsRaw.padEnd(4, '');
    const mm1 = digits[0] ?? '';
    const mm2 = digits[1] ?? '';
    const ss1 = digits[2] ?? '';
    const ss2 = digits[3] ?? '';

    // Fixed widths to match font-mono character spacing exactly
    const slotClass = (filled: boolean) => (filled ? 'text-gray-900' : 'text-gray-300');

    return (
      <div className="pointer-events-none absolute inset-0 flex items-center px-1.5 py-1.5 font-mono tabular-nums text-sm leading-none">
        <span className={clsx('w-[1ch] text-center', slotClass(!!mm1))}>{mm1 || t('roast.stageData.mask.minute')}</span>
        <span className={clsx('w-[1ch] text-center', slotClass(!!mm2))}>{mm2 || t('roast.stageData.mask.minute')}</span>
        <span className="w-[1ch] text-center font-bold text-gray-900">:</span>
        <span className={clsx('w-[1ch] text-center', slotClass(!!ss1))}>{ss1 || t('roast.stageData.mask.second')}</span>
        <span className={clsx('w-[1ch] text-center', slotClass(!!ss2))}>{ss2 || t('roast.stageData.mask.second')}</span>
      </div>
    );
  };

  return (
    <div className="bg-white border rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-700 mb-4">
        {showTargetLabels ? t('roast.stageData.targetStageData') : t('roast.stageData.stageData')}
      </h4>

      <div className="space-y-3">
        {stages.map((stage, index) => (
          <div key={stage.key} className="grid grid-cols-12 items-center gap-2 sm:gap-4">
            <div className="col-span-4 sm:col-span-3">
              <label className="text-sm font-medium text-gray-700 truncate">{stage.label}</label>
            </div>

            <div className="col-span-4 sm:col-span-4">
              {(() => {
                const isActive = activeTimeKey === stage.key;
                const baseDigits = getTimeDigits(stage.time);
                const digits = isActive ? (timeDrafts[stage.key] ?? baseDigits) : baseDigits;
                
                // Construct the actual input value to include the colon if we have > 2 digits
                // This ensures the browser's caret position naturally skips the colon in the overlay
                const inputValue = digits.length > 2 
                  ? `${digits.slice(0, 2)}:${digits.slice(2)}` 
                  : digits;

                return (
                  <div className="relative">
                    <input
                      ref={el => {
                        timeInputRefs.current[stage.key] = el;
                      }}
                      data-test-id={`roast-stage-time-${stage.key}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      disabled={readOnly}
                      value={inputValue}
                      onFocus={() => {
                        setActiveTimeKey(stage.key);
                        setTimeDrafts(prev => ({ ...prev, [stage.key]: getTimeDigits(stage.time) }));
                        setTimeout(() => ensureCaretAtEnd(stage.key), 0);
                      }}
                      onClick={() => ensureCaretAtEnd(stage.key)}
                      onKeyUp={() => ensureCaretAtEnd(stage.key)}
                      onChange={event => {
                        const nextDigits = getTimeDigits(event.target.value);
                        setTimeDrafts(prev => ({ ...prev, [stage.key]: nextDigits }));
                        // Ensure caret stays at the end after the value is reformatted with a colon
                        setTimeout(() => ensureCaretAtEnd(stage.key), 0);
                      }}
                      onBlur={() => {
                        const finalDigits = timeDrafts[stage.key] ?? getTimeDigits(stage.time);
                        const formatted = formatStageTimeFromDigits(finalDigits);
                        onStageChange(index, 'time', formatted);
                        setActiveTimeKey(prev => (prev === stage.key ? null : prev));
                        setTimeDrafts(prev => {
                          const next = { ...prev };
                          delete next[stage.key];
                          return next;
                        });
                      }}
                      className={clsx(
                        'block w-full border-gray-300 rounded-md border p-1.5 text-sm font-mono tabular-nums',
                        'bg-white text-transparent caret-gray-900 focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                      )}
                    />
                    {renderTimeOverlay(digits)}
                  </div>
                );
              })()}
            </div>

            <div className="col-span-4 sm:col-span-5 flex items-center gap-1 sm:gap-3">
              <label className="hidden sm:block text-sm font-medium text-gray-700 whitespace-nowrap">{t('roast.stageData.temperature')}</label>

              <div className="relative flex-1">
                <input
                  type="number"
                  max={MAX_ROAST_TEMP_C}
                  value={stage.temp}
                  disabled={readOnly}
                  onChange={event => onStageChange(index, 'temp', clampTemperatureInput(event.target.value))}
                  placeholder={t('roast.agtron.zeroPlaceholder')}
                  className="block w-full text-sm border-gray-300 rounded-md border p-1.5 pr-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {t('roast.stageData.celsius')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
